import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Heart, Shield, Database, TrendingUp, Globe, Zap, CheckCircle, Loader2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import { useTranslation } from 'react-i18next';
import { toast } from "sonner";

/** Preset donation amounts in cents; null = customer-entered amount. */
const DONATION_PRESETS: Array<{ cents: number | null; label: string }> = [
  { cents: 1000, label: "$10" },
  { cents: 2500, label: "$25" },
  { cents: 10000, label: "$100" },
  { cents: null, label: "" }, // custom — label from i18n below
];

const Donate = () => {
  const navigate = useNavigate();
  const brand = useBrand();
  const { isDark } = useTheme();

  const { t } = useTranslation(['donate', 'common']);
  const isAhp = brand.key === 'ahp';
  const ahpDark = isAhp && isDark;
  const [donating, setDonating] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  // Back from a completed Stripe checkout (?thanks=1) — say thank you.
  useEffect(() => {
    if (searchParams.get('thanks')) {
      toast.success(t('donate:donation.thanks', { defaultValue: 'Thank you for supporting price transparency! ❤️' }), { duration: 6000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startDonation = async (cents: number | null, key: string) => {
    setDonating(key);
    try {
      const res = await fetch('/api/create-donation-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(cents ? { amount: cents } : {}),
          success_url: `${window.location.origin}/donate?thanks=1`,
          cancel_url: `${window.location.origin}/donate`,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) throw new Error(data.error || `HTTP ${res.status}`);
      window.location.href = data.url;
    } catch (e) {
      toast.error(t('donate:donation.error', { defaultValue: "Couldn't open the payment page — please try again." }));
      setDonating(null);
    }
  };

  return (
    <>
    <Helmet>
      <title>{t('donate:meta.pageTitle', { brandName: brand.name })}</title>
      <meta name="description" content={t('donate:meta.description')} />
      <meta name="keywords" content="support healthcare transparency, donate healthcare, american hospital prices, healthcare pricing platform" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={`${brand.primaryDomain}/donate`} />
      <meta property="og:title" content={t('donate:meta.pageTitle', { brandName: brand.name })} />
      <meta property="og:description" content={t('donate:meta.ogDescription')} />
      <meta property="og:image" content={brand.social.ogImage} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${brand.primaryDomain}/donate`} />
      <meta property="og:site_name" content={brand.name} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={t('donate:meta.pageTitle', { brandName: brand.name })} />
      <meta name="twitter:description" content={t('donate:meta.ogDescription')} />
      <meta name="twitter:image" content={brand.social.ogImage} />
      {brand.social.twitter && <meta name="twitter:site" content={brand.social.twitter} />}
    </Helmet>
    <div className={`min-h-screen py-12 px-4 pb-24 ${
      isAhp
        ? ahpDark ? 'bg-[#0a1628]' : 'bg-[#FAF9F6]'
        : brand.key === 'antigreed'
          ? 'bg-gradient-to-br from-amber-50 to-orange-50'
          : 'bg-gradient-to-br from-background via-background/95 to-primary/5'
    }`}>
      <div className="max-w-4xl mx-auto">

        {/* Hero */}
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6 ${
            isAhp
              ? ahpDark ? 'bg-[#002868]/30 text-[#7DA2D1]' : 'bg-[#002868]/10 text-[#002868]'
              : brand.key === 'antigreed'
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-primary/10 text-primary'
          }`}>
            <Heart className="w-4 h-4" />
            {t('donate:hero.badge')}
          </div>
          <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${
            isAhp
              ? ahpDark ? 'text-[#FAF9F6]' : 'text-[#1B1B1B]'
              : brand.key === 'antigreed'
                ? 'text-emerald-800 font-serif'
                : 'text-foreground'
          }`}>
            {t('donate:hero.title')}{' '}
            <span className={isAhp ? 'text-[#B22234]' : 'text-primary'}>
              {t('donate:hero.titleHighlight')}
            </span>
          </h1>
          <p className={`text-xl max-w-2xl mx-auto ${
            isAhp ? (ahpDark ? 'text-gray-300' : 'text-gray-600') : brand.key === 'antigreed' ? 'text-gray-700' : 'text-muted-foreground'
          }`}>
            {t('donate:hero.subtitle', { brandName: brand.name })}
          </p>
        </div>

        {/* What we're building */}
        <Card className={`mb-8 ${
          isAhp ? (ahpDark ? 'bg-[#0d1e35] border-[#1a3050] shadow-sm' : 'bg-white border-gray-200 shadow-sm') : brand.key === 'antigreed' ? 'bg-white/90 border-amber-200' : ''
        }`}>
          <CardHeader>
            <CardTitle className={`text-2xl ${
              isAhp ? (ahpDark ? 'text-[#FAF9F6]' : 'text-[#1B1B1B]') : brand.key === 'antigreed' ? 'text-emerald-800 font-serif' : ''
            }`}>
              {t('donate:building.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className={`text-base mb-6 ${isAhp ? (ahpDark ? 'text-gray-300' : 'text-gray-700') : 'text-muted-foreground'}`}>
              {t('donate:building.description')}
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {[
                {
                  icon: Database,
                  title: t('donate:building.features.database.title'),
                  description: t('donate:building.features.database.description')
                },
                {
                  icon: TrendingUp,
                  title: t('donate:building.features.metrics.title'),
                  description: t('donate:building.features.metrics.description')
                },
                {
                  icon: Globe,
                  title: t('donate:building.features.international.title'),
                  description: t('donate:building.features.international.description')
                },
                {
                  icon: Zap,
                  title: t('donate:building.features.ai.title'),
                  description: t('donate:building.features.ai.description')
                }
              ].map((item, i) => (
                <div
                  key={i}
                  className={`p-5 rounded-lg border ${
                    isAhp
                      ? ahpDark ? 'bg-[#0a1628] border-[#1a3050]' : 'bg-[#FAF9F6] border-gray-200'
                      : brand.key === 'antigreed'
                        ? 'bg-white/90 border-amber-200'
                        : 'bg-card border-border'
                  }`}
                >
                  <item.icon className={`w-7 h-7 mb-3 ${
                    isAhp ? (ahpDark ? 'text-[#7DA2D1]' : 'text-[#002868]') : brand.key === 'antigreed' ? 'text-emerald-700' : 'text-primary'
                  }`} />
                  <h4 className={`font-semibold mb-1.5 ${isAhp ? (ahpDark ? 'text-[#FAF9F6]' : 'text-[#1B1B1B]') : ''}`}>{item.title}</h4>
                  <p className={`text-sm ${isAhp ? (ahpDark ? 'text-gray-400' : 'text-gray-600') : 'text-muted-foreground'}`}>
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Where donations go */}
        <Card className={`mb-8 ${
          isAhp ? (ahpDark ? 'bg-[#0d1e35] border-[#1a3050] shadow-sm' : 'bg-white border-gray-200 shadow-sm') : brand.key === 'antigreed' ? 'bg-white/90 border-amber-200' : ''
        }`}>
          <CardHeader>
            <CardTitle className={`text-2xl ${
              isAhp ? (ahpDark ? 'text-[#FAF9F6]' : 'text-[#1B1B1B]') : brand.key === 'antigreed' ? 'text-emerald-800 font-serif' : ''
            }`}>
              {t('donate:support.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[
                {
                  label: t('donate:support.items.aiProcessing.label'),
                  detail: t('donate:support.items.aiProcessing.detail')
                },
                {
                  label: t('donate:support.items.infrastructure.label'),
                  detail: t('donate:support.items.infrastructure.detail')
                },
                {
                  label: t('donate:support.items.consumerTools.label'),
                  detail: t('donate:support.items.consumerTools.detail')
                },
                {
                  label: t('donate:support.items.dataExpansion.label'),
                  detail: t('donate:support.items.dataExpansion.detail')
                }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                    isAhp ? (ahpDark ? 'text-[#7DA2D1]' : 'text-[#002868]') : brand.key === 'antigreed' ? 'text-emerald-600' : 'text-primary'
                  }`} />
                  <div>
                    <span className={`font-medium ${isAhp ? (ahpDark ? 'text-[#FAF9F6]' : 'text-[#1B1B1B]') : ''}`}>{item.label}</span>
                    <span className={`text-sm block ${isAhp ? (ahpDark ? 'text-gray-400' : 'text-gray-600') : 'text-muted-foreground'}`}>
                      {item.detail}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Independence promise */}
        <div className={`p-6 rounded-lg border-l-4 mb-8 ${
          isAhp
            ? ahpDark ? 'bg-[#002868]/20 border-[#7DA2D1]' : 'bg-[#002868]/5 border-[#002868]'
            : brand.key === 'antigreed'
              ? 'bg-emerald-50 border-emerald-600'
              : 'bg-primary/5 border-primary'
        }`}>
          <div className="flex items-start gap-3">
            <Shield className={`w-6 h-6 mt-0.5 flex-shrink-0 ${
              isAhp ? (ahpDark ? 'text-[#7DA2D1]' : 'text-[#002868]') : brand.key === 'antigreed' ? 'text-emerald-700' : 'text-primary'
            }`} />
            <div>
              <h3 className={`text-lg font-semibold mb-2 ${isAhp ? (ahpDark ? 'text-[#FAF9F6]' : 'text-[#1B1B1B]') : ''}`}>
                {t('donate:independencePromise.title')}
              </h3>
              <p className={`text-sm ${isAhp ? (ahpDark ? 'text-gray-300' : 'text-gray-700') : 'text-muted-foreground'}`}>
                {t('donate:independencePromise.description', { brandName: brand.name })}
              </p>
            </div>
          </div>
        </div>

        {/* Donation buttons */}
        <Card className={`mb-8 ${
          isAhp ? (ahpDark ? 'bg-[#0d1e35] border-[#1a3050] shadow-sm' : 'bg-white border-gray-200 shadow-sm') : brand.key === 'antigreed' ? 'bg-white/90 border-amber-200' : ''
        }`}>
          <CardContent className="pt-6">
            <h3 className={`text-xl font-semibold text-center mb-6 ${isAhp ? (ahpDark ? 'text-[#FAF9F6]' : 'text-[#1B1B1B]') : ''}`}>
              {t('donate:donation.title', { brandName: brand.name })}
            </h3>

            {/* Live Stripe checkout — preset amounts + customer-entered amount.
                (Was an "opening soon" placeholder until the account was
                approved and /api/create-donation-session went live 2026-07-10.) */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {DONATION_PRESETS.map((p) => {
                const key = p.cents ? String(p.cents) : 'custom';
                const label = p.cents ? p.label : t('donate:donation.customAmount', { defaultValue: 'Choose amount' });
                const busy = donating === key;
                return (
                  <Button
                    key={key}
                    disabled={donating !== null}
                    onClick={() => startDonation(p.cents, key)}
                    className={`h-14 text-base font-bold ${
                      isAhp
                        ? ahpDark
                          ? 'bg-[#C5A55A] text-[#0a1628] hover:bg-[#d4b76a]'
                          : 'bg-[#002868] text-white hover:bg-[#0a3a8a]'
                        : brand.key === 'antigreed' ? 'bg-emerald-700 text-white hover:bg-emerald-800' : ''
                    }`}
                  >
                    {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : label}
                  </Button>
                );
              })}
            </div>
            <p className={`text-xs text-center mt-4 ${isAhp ? (ahpDark ? 'text-gray-400' : 'text-gray-600') : 'text-muted-foreground'}`}>
              {t('donate:donation.securityNote', { defaultValue: 'Secure checkout via Stripe. No account needed.' })}
            </p>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="text-center space-x-3">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className={isAhp ? (ahpDark ? 'border-gray-600 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50') : brand.key === 'antigreed' ? 'border-emerald-700 text-emerald-700 hover:bg-emerald-50' : ''}
          >
            {t('donate:navigation.back')}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className={isAhp ? (ahpDark ? 'border-gray-600 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50') : brand.key === 'antigreed' ? 'border-emerald-700 text-emerald-700 hover:bg-emerald-50' : ''}
          >
            {t('donate:navigation.home')}
          </Button>
        </div>
      </div>
    </div>
    </>
  );
};

export default Donate;
